import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { Check, Inbox, Pencil, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select-native';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import PageHeader from './shell/PageHeader';

const DIFFICULTY_LABELS = {
  1: 'Easy',
  2: 'Medium',
  3: 'Challenging',
  4: 'Hard',
  5: 'Very hard',
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
      {label}
    </span>
    {children}
  </div>
);

const Stars = ({ level }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={cn('h-3 w-3', i < level ? 'fill-heat text-heat' : 'text-muted-foreground/40')}
      />
    ))}
  </span>
);

const MapSubmissionReview = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [working, setWorking] = useState(false);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'mapSubmissions'), where('status', '==', 'pending'))
      );
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const am = a.createdAt?.toMillis?.() ?? 0;
        const bm = b.createdAt?.toMillis?.() ?? 0;
        return am - bm;
      });
      setSubmissions(rows);
    } catch (err) {
      toast.error('Could not load submissions.', { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const openDetail = (sub) => {
    setSelected(sub);
    setEditForm({
      mapName: sub.mapName || '',
      mapper: sub.mapper || '',
      tier: sub.tier || 1,
      difficulty: sub.difficulty || 1,
      hintText: sub.hintText || '',
    });
  };

  const closeDetail = () => {
    setSelected(null);
    setEditForm(null);
  };

  const handleApprove = async () => {
    if (!selected || !editForm) return;
    if (!editForm.mapName.trim()) {
      toast.error('Map name is required.');
      return;
    }
    setWorking(true);
    try {
      await addDoc(collection(db, 'mapImages'), {
        mapName: editForm.mapName.trim(),
        mapper: editForm.mapper.trim(),
        tier: Number(editForm.tier) || 1,
        difficulty: Number(editForm.difficulty) || 1,
        hintText: editForm.hintText.trim(),
        isActive: true,
        imageUrl: selected.imageUrl,
        storageFilePath: selected.storageFilePath,
        addedBy: selected.submittedBy,
        approvedBy: user?.uid || 'admin',
        submissionId: selected.id,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      await updateDoc(doc(db, 'mapSubmissions', selected.id), {
        status: 'approved',
        reviewedBy: user?.uid || 'admin',
        reviewedAt: serverTimestamp(),
      });

      toast.success('Submission approved — added to library.');
      closeDetail();
      await fetchPending();
    } catch (err) {
      toast.error('Approve failed.', { description: err.message });
    } finally {
      setWorking(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setWorking(true);
    try {
      if (rejectTarget.storageFilePath) {
        try {
          await deleteObject(ref(storage, rejectTarget.storageFilePath));
        } catch {
          // non-fatal — file may already be gone
        }
      }
      await deleteDoc(doc(db, 'mapSubmissions', rejectTarget.id));
      toast.success('Submission rejected.');
      setRejectTarget(null);
      closeDetail();
      await fetchPending();
    } catch (err) {
      toast.error('Reject failed.', { description: err.message });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="pb-16">
      <PageHeader
        eyebrow="Maps & media"
        title="Map submissions"
        description="Review community-submitted map screenshots. Approved submissions go straight into the map image library and can appear in the daily game."
      />

      <Card className="border-border/60 bg-background/40">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-lg">Pending · {submissions.length}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="divide-y divide-border/60 border-y border-border/60">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex h-20 animate-pulse items-center gap-4 px-2">
                  <div className="h-14 w-20 bg-muted" />
                  <div className="h-4 w-40 bg-muted" />
                  <div className="ml-auto h-3 w-20 bg-muted" />
                </div>
              ))}
            </div>
          ) : submissions.length === 0 ? (
            <div className="flex flex-col items-center gap-3 border border-dashed border-border/60 px-6 py-14 text-center">
              <Inbox className="h-8 w-8 text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">No pending submissions right now.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60 border-y border-border/60">
              {submissions.map((sub) => (
                <li
                  key={sub.id}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-2 py-3"
                >
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted/20">
                    <img src={sub.imageUrl} alt="" className="h-full w-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-display text-base font-medium">{sub.mapName}</div>
                    <div className="mt-1 flex items-center gap-3">
                      <Stars level={sub.difficulty || 1} />
                      <Badge variant="outline" className="uppercase">
                        Tier {sub.tier || '?'}
                      </Badge>
                      <span className="truncate font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                        by {sub.submitterName || 'Anonymous'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(sub)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Review
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Review submission</DialogTitle>
          </DialogHeader>

          {selected && editForm && (
            <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-3">
                <div className="overflow-hidden rounded-md border border-border/60 bg-muted/20">
                  <img src={selected.imageUrl} alt="" className="h-64 w-full object-cover" />
                </div>
                <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                  <span>
                    Submitted by{' '}
                    <span className="text-foreground">{selected.submitterName || 'Anonymous'}</span>
                  </span>
                  {selected.submitterEmail && <span>{selected.submitterEmail}</span>}
                </div>
              </div>

              <div className="grid gap-4">
                <Field label="Map name">
                  <Input
                    value={editForm.mapName}
                    onChange={(e) => setEditForm({ ...editForm, mapName: e.target.value })}
                  />
                </Field>
                <Field label="Mapper">
                  <Input
                    value={editForm.mapper}
                    onChange={(e) => setEditForm({ ...editForm, mapper: e.target.value })}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Tier">
                    <NativeSelect
                      value={editForm.tier}
                      onChange={(e) => setEditForm({ ...editForm, tier: Number(e.target.value) })}
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((t) => (
                        <option key={t} value={t}>
                          Tier {t}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                  <Field label="Guess difficulty">
                    <NativeSelect
                      value={editForm.difficulty}
                      onChange={(e) =>
                        setEditForm({ ...editForm, difficulty: Number(e.target.value) })
                      }
                    >
                      {[1, 2, 3, 4, 5].map((lvl) => (
                        <option key={lvl} value={lvl}>
                          {lvl} — {DIFFICULTY_LABELS[lvl]}
                        </option>
                      ))}
                    </NativeSelect>
                  </Field>
                </div>
                <Field label="Hint">
                  <Textarea
                    rows={2}
                    value={editForm.hintText}
                    onChange={(e) => setEditForm({ ...editForm, hintText: e.target.value })}
                  />
                </Field>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="destructive"
              disabled={working}
              onClick={() => setRejectTarget(selected)}
            >
              <X className="h-4 w-4" />
              Reject
            </Button>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={closeDetail}>
                Close
              </Button>
              <Button type="button" disabled={working} onClick={handleApprove}>
                <Check className="h-4 w-4" />
                {working ? 'Approving…' : 'Approve & publish'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject submission?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Permanently removes{' '}
            <span className="font-medium text-foreground">{rejectTarget?.mapName}</span> and deletes
            the uploaded image.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={working}>
              {working ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapSubmissionReview;
